import { Component } from 'react'
import { withRouter } from 'next/router'
import { montserrat } from 'styles/variables'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive'

//* Components
import BaseTheme from '../Theme'
import Text from 'components/common/Text'
//* HOC's
import { withUIContext } from 'context'

class LineChart extends Component {
    makeChart() {
        //! UI data
        this.data = {
            series: [],
            values: [],
        }

        this.props.data.map((item, key) => {
            this.data['series'][key] = {
                title: item['title'],
                key: item['title'],
                color: item['additional_info']['color'],
            }

            item.additional_info.values.map((value) => {
                let dateKey = value['date']

                this.data['values'] = {
                    ...this.data['values'],
                    [dateKey]: {
                        ...this.data['values']?.[dateKey],
                        date: new Date(dateKey).getTime(),
                        [item.title]: Number(value['value']),
                    },
                }
            })
        })

        this.data['values'] = Object.values(this.data['values'])

        // Create root element
        // https://www.amcharts.com/docs/v5/getting-started/#Root_element
        const root = am5.Root.new(this.props.id)
        root._logo.dispose()

        root.suffix = this.props.suffix || ''

        let legendRoot = am5.Root.new(this.props.legendId)
        legendRoot._logo.dispose()

        const responsive = am5themes_Responsive.new(root)

        // Set themes
        // https://www.amcharts.com/docs/v5/concepts/themes/
        root.setThemes([am5themes_Animated.new(root), responsive, BaseTheme.new(root, {})])

        // Create chart
        // https://www.amcharts.com/docs/v5/charts/xy-chart/
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: true,
                panY: true,
                wheelX: 'panX',
                wheelY: 'zoomX',
                pinchZoomX: true,
                focusable: true,
                strokeWidth: 2,
                strokeOpacity: 1,
                stroke: am5.color('#FFF'),
            })
        )

        // Create axes
        // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
        const xRenderer = am5xy.AxisRendererX.new(root, {
            minGridDistance: 30,
        })

        xRenderer.labels.template.setAll({
            paddingTop: 15,
        })

        xRenderer.grid.template.set('visible', false)

        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                maxDeviation: 0.3,
                baseInterval: {
                    timeUnit: 'year',
                    count: 1,
                },
                renderer: xRenderer,
            })
        )

        const yRenderer = am5xy.AxisRendererY.new(root, {})

        yRenderer.grid.template.setAll({
            stroke: am5.color('#779997'),
            strokeWidth: 1,
        })

        yRenderer.labels.template.setAll({
            paddingRight: 15,
        })

        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                maxDeviation: 0.3,
                renderer: yRenderer,
            })
        )

        // Add cursor
        // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
        var cursor = chart.set(
            'cursor',
            am5xy.XYCursor.new(root, {
                xAxis: xAxis,
            })
        )

        cursor.lineY.set('visible', false)

        // Add scrollbar
        // https://www.amcharts.com/docs/v5/charts/xy-chart/scrollbars/
        chart.set(
            'scrollbarX',
            am5.Scrollbar.new(root, {
                orientation: 'horizontal',
            })
        )

        // Set date fields
        // https://www.amcharts.com/docs/v5/concepts/data/#Parsing_dates
        root.dateFormatter.setAll({
            dateFormat: 'yyyy',
            dateFields: ['valueX'],
        })

        // Create legendRoot and legend
        const legend = legendRoot.container.children.push(
            am5.Legend.new(legendRoot, {
                centerX: am5.percent(50),
                width: am5.percent(100),
                x: am5.percent(50),
                y: am5.percent(0),
                paddingLeft: 20,
                paddingTop: 15,
                nameField: 'valueX',
                layout: am5.GridLayout.new(root, {
                    maxColumns: 2,
                    fixedWidthGrid: true,
                    width: am5.percent(100),
                }),
            })
        )

        legend.labels.template.setAll({
            fill: '#ffffff',
            fontSize: 16,
            fontFamily: montserrat.style.fontFamily,
        })

        const makeSeries = (name, fieldName, color) => {
            // Add series
            // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
            const series = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    stroke: color,
                    valueYField: fieldName,
                    valueXField: 'date',
                    tooltip: am5.Tooltip.new(root, {
                        pointerOrientation: 'horizontal',
                        labelText: `{date.formatDate("YYYY")}: {valueY} ${root.suffix}`,
                    }),
                })
            )

            series.strokes.template.setAll({
                strokeWidth: 2,
                stroke: color,
                tooltipText: "{categoryX}:{valueYWorking.formatNumber('#.#')}",
            })

            series.data.processor = am5.DataProcessor.new(root, {
                dateFormat: 'yyyy',
                dateFields: ['valueX'],
            })

            // Add Label bullet
            series.bullets.push(function (root, series, dataItem) {
                const circle = am5.Circle.new(root, {
                    radius: 4,
                    fill: color,
                    stroke: color,
                    strokeWidth: 2,
                })

                return am5.Bullet.new(root, {
                    locationX: 0.5,
                    locationY: 0,
                    sprite: circle,
                })
            })

            series.data.setAll(this.data.values)
            legend.data.push(series)
            series.appear(1000, 100)
        }

        this.data.series.map((item) => {
            makeSeries(item.title, item.key, item.color)
        })

        // !Make stuff animate on load
        // https://www.amcharts.com/docs/v5/concepts/animations/
        chart.appear(1000, 100)

        // !Responsive rules
        responsive.addRule({
            relevant: am5themes_Responsive.widthXXL,
            applying: function () {
                yRenderer.labels.template.setAll({
                    fontSize: 16,
                    paddingRight: 15,
                })

                xRenderer.labels.template.setAll({
                    fontSize: 16,
                    paddingTop: 15,
                })
            },
        })

        responsive.addRule({
            relevant: am5themes_Responsive.widthXL,
            applying: function () {
                yRenderer.labels.template.setAll({
                    fontSize: 14,
                    paddingRight: 15,
                })

                xRenderer.labels.template.setAll({
                    fontSize: 14,
                    paddingTop: 15,
                })
            },
        })

        responsive.addRule({
            relevant: am5themes_Responsive.widthL,
            applying: function () {
                yRenderer.labels.template.setAll({
                    fontSize: 14,
                    paddingRight: 10,
                })

                xRenderer.labels.template.setAll({
                    fontSize: 14,
                    paddingTop: 10,
                })
            },
        })

        responsive.addRule({
            relevant: am5themes_Responsive.widthM,
            applying: function () {
                yRenderer.labels.template.setAll({
                    fontSize: 13,
                    paddingRight: 10,
                })

                xRenderer.labels.template.setAll({
                    fontSize: 13,
                    paddingTop: 10,
                })

                legend.labels.template.setAll({
                    fontSize: 13,
                })
            },
        })

        this.root = root
        this.legendRoot = legendRoot
    }

    componentDidMount() {
        this.makeChart()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.router.locale != this.props.router.locale) {
            if (this.root) {
                this.root.dispose()
            }

            if (this.legendRoot) {
                this.legendRoot.dispose()
            }

            this.makeChart()
        }
    }

    componentWillUnmount() {
        if (this.root) {
            this.root.dispose()
        }

        if (this.legendRoot) {
            this.legendRoot.dispose()
        }
    }

    render() {
        return (
            <div className='base-chart-wrapper'>
                <div
                    className={`base-chart with-legend ${this.props.className || ''}`}
                    id={this.props.id}
                ></div>
                {this.props.description && <Text className={'chart-desc p p-1 white font-montserrat'}>{this.props.description}</Text>}
                {this.props.legendId && <div id={this.props.legendId}></div>}
            </div>
        )
    }
}

export default withUIContext(withRouter(LineChart), ['winWidth'])
